import { useDispatch, useSelector } from 'react-redux'

import { useGetPolicyQuery, useGetUserQuery } from 'common'
import { isUserGroupsEnabledSelector } from 'features/user_groups'
import { useHomeTabs } from 'hooks/home/tabs'
import {
  hasAnyUserGroupsSelector,
  userGroupsWhereAdminSelector,
  isFilteringSelector,
  usersSelector,
  policySelector
} from 'selectors/home_bar'

import PrimaryList from './primary_list'
import TeamsList from './teams_list'
import UserGroupList from './user_group_list'
import UserGroupsList from './user_groups_list'
import UsersList from './users_list'

const List = () => {
  const dispatch = useDispatch()
  const isFiltering = useSelector(isFilteringSelector)
  const { setTabScopeV1 } = useHomeTabs()
  const { scopes } = useSelector(policySelector)
  const { data: { canCreateTeam, canManageInvitation } } = useGetPolicyQuery()
  const { data: currentUser } = useGetUserQuery()
  const {
    is_user_group_admin: isGroupAdmin,
    user_group_relationships: userGroupRelationships,
    organization_membership: {
      is_admin_or_owner: isAdminOrOwner
    } = {}
  } = currentUser
  const dropdownEntityGroups = Object.keys(scopes).filter((k) => scopes[k])

  const shouldRenderTeams = dropdownEntityGroups.includes('page')

  const userGroupsEnabled = useSelector(isUserGroupsEnabledSelector)
  const anyUserGroups = useSelector(hasAnyUserGroupsSelector)

  const allUserGroupsWhereAdmin = Object.values(userGroupRelationships || []).filter((rel) => rel.admin)
  const userGroupAdminOfMultipleGroups = isGroupAdmin && allUserGroupsWhereAdmin.length > 1
  const filteredUserGroupsWhereAdmin = useSelector(userGroupsWhereAdminSelector)
  const filteredUsers = useSelector(usersSelector)

  const shouldShowAllUsers = dropdownEntityGroups.includes('all_users')
  const shouldRenderUsers = shouldShowAllUsers
    || userGroupAdminOfMultipleGroups
    // We should render users if the current user is a group admin but is filtering on a term that matches
    // user names and not group names - in this case we would not see groups returned but should still see users
    || (filteredUserGroupsWhereAdmin.length === 0 && filteredUsers.length > 0)
  const shouldRenderOneUserGroup = !shouldRenderUsers
    && userGroupsEnabled
    && isGroupAdmin
    && filteredUserGroupsWhereAdmin.length === 1
    && filteredUsers.length > 0
  const shouldRenderManyUserGroups = anyUserGroups
    && userGroupsEnabled
    && (
      isAdminOrOwner
      || userGroupAdminOfMultipleGroups
      || (filteredUserGroupsWhereAdmin.length > 0 && filteredUsers.length === 0)
    )

  const selectItem = (scopeType, scopeId) => {
    dispatch({
      type: 'SCOPE_DROPDOWN_UPDATE_FILTER_TERM',
      payload: ''
    })
    dispatch(setTabScopeV1({ type: scopeType, id: scopeId }))
  }

  return (
    <>
      {!isFiltering && (
        <PrimaryList
          selectItem={selectItem}
          shouldRenderUsersAndTeams={shouldShowAllUsers && shouldRenderTeams}
        />
      )}
      {shouldRenderOneUserGroup && (
        <UserGroupList
          selectItem={selectItem}
          userGroup={filteredUserGroupsWhereAdmin[0]}
        />
      )}
      {shouldRenderManyUserGroups && (
        <UserGroupsList
          selectItem={selectItem}
          canCreateNewGroups={isAdminOrOwner && !isGroupAdmin}
        />
      )}
      {shouldRenderUsers && (
        <UsersList
          shouldShowAll={shouldShowAllUsers}
          selectItem={selectItem}
          canManageInvitation={canManageInvitation}
        />
      )}
      {shouldRenderTeams && (
        <TeamsList
          selectItem={selectItem}
          canCreateTeam={canCreateTeam}
          isAdminOrOwner={isAdminOrOwner}
        />
      )}
    </>
  )
}

export default List
